import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useQuery } from "../../../common/hooks";
import { Routes } from "../../../common/Routes";
import { firstUpperCase, inProgress, isNullOrUndefined } from "../../../common/utils";
import {
  sendUserGroupDetailsEvent,
  UserGroupDetailsAction,
} from "../../../components/matomoClient";
import { useGetUserGroupTypeId } from "../hooks/GetUserGroupTypeId";
import { useVerifyUserGroupType } from "../hooks/VerifyUserGroupType";
import { ListUserGroupTabNameQueryParam, listUserGroupTabTypes } from "../listUserGroups/model";
import { UserGroupActions } from "../model";
import { fromTabQueryParam, userGroupDetailsTabTypes } from "./model";
import { membersKey, ownersKey } from "./userGroupInfo/model";
import { useHandleInvalidUserGroup } from "../hooks/HandleInvalidUserGroup";
import { invert } from "lodash";

export const UserTabKeyVsRoutes = {
  "Access Visibility": "access-visibility",
};
const UserRoutesVsTabKey = invert(UserTabKeyVsRoutes);

export const UserGroupDetailsController = (userGroupUniqueName, userGroupTypeUniqueName) => {
  const dispatch = useDispatch();
  const history = useHistory();
  let query = useQuery();

  const fromTabArray = useState(query.get(fromTabQueryParam));
  const fromTab = fromTabArray[0];

  const userGroupTypeId = useGetUserGroupTypeId(userGroupTypeUniqueName);
  useVerifyUserGroupType(userGroupTypeUniqueName);

  const userGroupTypeProgress = useSelector((rootState) => rootState.userGroup.types.progress);
  const userGroupDetailProgress = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.progress
  );

  const userGroupTypeError = useSelector((rootState) => rootState.userGroup.types.error);
  const userGroupDetailError = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.error
  );

  let userGroupRepresentationName = useSelector(
    (rootState) => rootState.userGroup.types.value[userGroupTypeId]?.columnName
  );
  let userGroupTypeName = useSelector(
    (rootState) => rootState.userGroup.types.value[userGroupTypeId]?.tabName
  );
  let userGroupTypeSingularFullName = useSelector(
    (rootState) => rootState.userGroup.types.value[userGroupTypeId]?.name
  );
  const userGroupDetails = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value
  );
  let activeTab = useSelector(
    (rootState) => rootState.userGroup.userGroupDetailsTabStates[userGroupDetails?.id]?.activeTab
  );
  useHandleInvalidUserGroup(userGroupUniqueName);

  const {
    params: { tabName },
  } = useRouteMatch();

  useEffect(() => {
    if(tabName){
      const tabKey = UserRoutesVsTabKey[tabName];
      if (tabKey && activeTab !== tabKey) {
        onTabChange(tabKey);
      }
    }else{
      handleNavigate(activeTab)
    }
  }, [tabName, activeTab]);

  const handleNavigate = (tabKey) => {
    const currentUrl = window.location.pathname.replace(/\/$/, "");
    const route = UserTabKeyVsRoutes[tabKey];
    if (route && !currentUrl.includes(route)) {
      const updatedUrl = route ? `${currentUrl}/${route}` : currentUrl;
      history.push(updatedUrl);
    } else if (!route) {
      const value = Object.values(UserTabKeyVsRoutes).find((val) => currentUrl.includes(val));
      onTabChange(tabKey);
      if (value) {
        history.push(currentUrl.substring(0, currentUrl.indexOf(value)));
      }
    }
  };

  const isPublished = userGroupDetails?.isPublished;

  let titleName = userGroupDetails?.name;
  userGroupRepresentationName = isNullOrUndefined(userGroupRepresentationName)
    ? ""
    : userGroupRepresentationName;

  const loading = inProgress(userGroupTypeProgress) || inProgress(userGroupDetailProgress);

  const errorFetchingDetails =
    !isNullOrUndefined(userGroupTypeError) || !isNullOrUndefined(userGroupDetailError);

  const scope = userGroupDetails?.scope?.name ?? "";

  let userGroupInfoTabProps = {
    description: userGroupDetails?.description,
    businessFunction: userGroupDetails?.functionalGroup,
    noOfMembers: userGroupDetails?.members?.length - userGroupDetails?.excludedMembers?.length,
    users: userGroupDetails?.owners,
    userGroupUniqueName,
    userGroupName: titleName,
    userGroupRepresentationName,
    userRoleKey: ownersKey,
    scope: firstUpperCase(scope),
  };

  let userGroupMembersTabProps = {
    users: userGroupDetails?.members,
    userGroupUniqueName,
    userGroupName: titleName,
    userGroupRepresentationName,
    userRoleKey: membersKey,
  };

  let usergroupMembershipConditionProps = {
    membershipCondition: userGroupDetails?.membershipCondition,
    individualInclusionMembers: userGroupDetails?.individualInclusionMembers,
    haveMembershipConditionsEditAccess: userGroupDetails?.haveMembershipConditionsEditAccess,
    haveMembershipConditionsViewAccess: userGroupDetails?.haveMembershipConditionsViewAccess,
    userGroupUniqueName,
    userGroupTypeUniqueName,
  };

  function onTabChange(activeTab) {
    dispatch(UserGroupActions.setActiveTab({ userGroupId: userGroupDetails?.id, activeTab }));
    sendUserGroupDetailsEvent(UserGroupDetailsAction.TabChange(titleName), activeTab);
  }

  let backToTabList = fromTab;
  if (isNullOrUndefined(backToTabList)) {
    backToTabList = listUserGroupTabTypes.MyList;
  }

  function backToUserGroupList() {
    history.push(
      `${
        Routes.UserGroups.basePath
      }/${userGroupTypeUniqueName}?${ListUserGroupTabNameQueryParam}=${backToTabList.trim()}`
    );
    sendUserGroupDetailsEvent(
      UserGroupDetailsAction.BackToUserGroupList(userGroupTypeName),
      titleName
    );
  }

  useEffect(() => {
    dispatch(UserGroupActions.getUserGroupDetails(userGroupUniqueName));
  }, [userGroupUniqueName]);

  useEffect(() => {
    const currentUrl = new URL(window.location);
    const params = new URLSearchParams(currentUrl.search);
    params.delete("fromTab");
    const newUrl = `${currentUrl.origin}${currentUrl.pathname}`;
    const newSearch = params.toString();
    const updatedUrl = newSearch ? `${newUrl}?${newSearch}` : newUrl;
    window.history.replaceState({}, "", updatedUrl);
  }, []);

  useEffect(() => {
    const tabLabels = [
      userGroupDetailsTabTypes.AccessVisibility,
      userGroupDetailsTabTypes.MembershipConditions,
      userGroupDetailsTabTypes.Info,
      userGroupDetailsTabTypes.Members,
    ];
    if ((activeTab === undefined || !tabLabels.includes(activeTab)) && isPublished) {
      dispatch(
        UserGroupActions.setActiveTab({
          userGroupId: userGroupDetails?.id,
          activeTab: userGroupDetailsTabTypes.AccessVisibility,
        })
      );
    } else if (
      (activeTab === undefined || !tabLabels.includes(activeTab)) &&
      usergroupMembershipConditionProps.haveMembershipConditionsViewAccess
    ) {
      dispatch(
        UserGroupActions.setActiveTab({
          userGroupId: userGroupDetails?.id,
          activeTab: userGroupDetailsTabTypes.MembershipConditions,
        })
      );
    }
  }, [
    isPublished,
    activeTab,
    usergroupMembershipConditionProps.haveMembershipConditionsViewAccess,
  ]);

  return {
    loading,
    errorFetchingDetails,
    userGroupRepresentationName,
    titleName,
    userGroupInfoTabProps,
    userGroupMembersTabProps,
    activeTab,
    handleNavigate,
    userGroupTypeName,
    userGroupTypeSingularFullName,
    backToUserGroupList,
    backToTabList,
    usergroupMembershipConditionProps,
    isPublished,
    userGroupIdentifier: loading
      ? null
      : {
          id: userGroupDetails?.id,
          type: userGroupDetails?.type,
        },
  };
};
